import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { AlertCircle, CheckCircle, Shield, Zap, Lock, Info, Share2 } from 'lucide-react';
import CountdownTimer from './CountdownTimer';
import ShareRide from './ShareRide';
import { isIOS } from '../lib/capacitor';

// Result Card Component
export default function ResultCard({ result, tier, isAuthenticated }) {
  const { t } = useTranslation();
  const [showShare, setShowShare] = useState(false);

  // Handle rate limit error
  if (result.rate_limited) {
    return (
      <div className="result-card result-limit">
        <div className="result-header">
          <AlertCircle className="result-icon warning" />
          <div>
            <h3>{t('limit.reached') || 'Daily Limit Reached'}</h3>
            <p>{t('limit.usedAll') || `You've used all ${result.limit} free lookups today`}</p>
            {result.resets_at && <CountdownTimer resetsAt={result.resets_at} />}
          </div>
        </div>
        {!isIOS() && (
          <div className="upgrade-prompt">
            <Zap size={20} />
            <div>
              <strong>{t('limit.upgradeTitle') || 'Upgrade for Unlimited Lookups'}</strong>
              <p>{t('limit.upgradeDesc') || 'Get unlimited basic lookups plus car images and VIN details'}</p>
            </div>
            <Link to="/pricing" className="btn btn-yellow">
              {t('limit.viewPlans') || 'View Plans'}
            </Link>
          </div>
        )}
      </div>
    );
  }

  const isTLC = result.is_tlc_vehicle;
  const vehicleInfo = result.vehicle_info;
  const tlcData = result.tlc_data;
  const carImageUrl = result.car_image;
  const colorInfo = vehicleInfo?.color_info;
  const isChicago = result.city === 'chicago';
  const lookupType = result.lookup_type || 'basic';
  const enhancedRemaining = result.enhanced_remaining;
  const isBasicLookup = lookupType === 'basic';
  const isNonRegistry = result.is_tlc_vehicle === null;

  // Get city-specific labels
  const getLicensedLabel = () => isChicago ? t('result.licensedChicago') : t('result.licensed');
  const getRegisteredLabel = () => isChicago ? t('result.registeredWithChicago') : t('result.registeredWith');
  const getNotFoundLabel = () => isChicago ? t('result.notFoundChicago') : t('result.notFound');
  const getLicenseDetailsLabel = () => isChicago ? t('result.bacpDetails') : t('result.tlcDetails');

  // Get color source label for display
  const getColorSourceLabel = () => {
    if (!colorInfo) return null;
    if (colorInfo.registered_color) return 'DMV Registered';
    if (colorInfo.detected_color) return `Detected (${colorInfo.detected_confidence}%)`;
    if (colorInfo.available_colors?.length > 0) return 'Factory Option';
    return null;
  };

  // Handle non-registry state result (vehicle info only, no license verification)
  if (isNonRegistry) {
    return (
      <div className="result-card result-unverified">
        <div className="result-header">
          <Info className="result-icon info" />
          <div>
            <h3>{t('nonRegistry.resultTitle', { state: result.state })}</h3>
            <p>{t('nonRegistry.resultSubtitle')}</p>
          </div>
        </div>

        {/* Car Image - same as verified results */}
        {vehicleInfo && vehicleInfo.make && (
          <div className="car-image-container">
            {carImageUrl && (
              <>
                <div className="car-image-shimmer shimmer" />
                <img
                  src={carImageUrl}
                  alt={`${vehicleInfo?.year} ${vehicleInfo?.make} ${vehicleInfo?.model}`}
                  onLoad={(e) => {
                    e.target.classList.add('loaded');
                    const shimmer = e.target.previousSibling;
                    if (shimmer) shimmer.style.display = 'none';
                  }}
                  onError={(e) => {
                    e.target.style.display = 'none';
                    const shimmer = e.target.previousSibling;
                    if (shimmer) shimmer.style.display = 'none';
                    e.target.nextSibling.classList.add('visible');
                  }}
                />
              </>
            )}
            <div className={`car-placeholder ${!carImageUrl ? 'visible' : ''}`}>
              <svg viewBox="0 0 640 280" className="car-silhouette">
                <path d="M120,200 L140,140 Q160,100 220,90 L420,90 Q480,100 510,140 L580,180 Q600,190 600,210 L600,220 L580,220 L570,200 Q560,180 520,180 L120,180 Q80,180 70,200 L60,220 L40,220 L40,210 Q40,190 60,180 Z" fill="#374151"/>
                <circle cx="160" cy="220" r="40" fill="#1f2937" stroke="#4b5563" strokeWidth="8"/>
                <circle cx="160" cy="220" r="15" fill="#6b7280"/>
                <circle cx="480" cy="220" r="40" fill="#1f2937" stroke="#4b5563" strokeWidth="8"/>
                <circle cx="480" cy="220" r="15" fill="#6b7280"/>
                <path d="M180,100 L230,95 L230,130 Q200,135 180,140 Z" fill="#60a5fa" opacity="0.6"/>
                <path d="M420,95 L470,100 L450,140 Q430,135 420,130 Z" fill="#60a5fa" opacity="0.6"/>
                <path d="M260,95 L380,95 L380,130 L260,130 Z" fill="#60a5fa" opacity="0.6"/>
                <rect x="560" y="145" width="30" height="20" rx="3" fill="#ef4444"/>
                <rect x="55" y="165" width="25" height="15" rx="3" fill="#fbbf24"/>
              </svg>
            </div>
            <div className="car-image-label">
              {vehicleInfo.year} {vehicleInfo.make} {vehicleInfo.model}
              {vehicleInfo.color && <span className="car-color"> ({vehicleInfo.color})</span>}
            </div>
          </div>
        )}

        {/* Vehicle Info Grid */}
        {vehicleInfo && vehicleInfo.make && (
          <div className="vehicle-info-card">
            <h4>{t('result.vehicleInfo')}</h4>
            <div className="info-grid">
              <div>
                <span className="label">{t('result.plate')}:</span>
                <span className="value plate-number">{result.plate}</span>
              </div>
              <div>
                <span className="label">{t('result.year')}:</span>
                <span className="value">{vehicleInfo.year}</span>
              </div>
              <div className="full-width">
                <span className="label">{t('result.vehicle')}:</span>
                <span className="value">{vehicleInfo.make} {vehicleInfo.model}</span>
              </div>
              {vehicleInfo.body_class && (
                <div className="full-width">
                  <span className="label">{t('result.body')}:</span>
                  <span className="value">{vehicleInfo.body_class}</span>
                </div>
              )}
              {vehicleInfo.color && (
                <div className="full-width">
                  <span className="label">{t('result.color')}:</span>
                  <span className="value">{vehicleInfo.color}</span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* No vehicle info found */}
        {(!vehicleInfo || !vehicleInfo.make) && (
          <div className="unverified-plate-display">
            <span className="label">{t('result.plate')}:</span>
            <span className="plate-number">{result.plate}</span>
            <span className="state-badge">{result.state}</span>
          </div>
        )}
        {(!vehicleInfo || !vehicleInfo.make) && (
          <div className="unverified-message">
            <p>{t('nonRegistry.noVehicleInfo')}</p>
          </div>
        )}

        {/* Disclaimer */}
        <div className="result-disclaimer">
          <AlertCircle size={12} />
          <span>{t('nonRegistry.disclaimer')}</span>
        </div>

        {/* Share Button */}
        {vehicleInfo && vehicleInfo.make ? (
          <div className="share-nudge-section">
            <div className="share-nudge">
              <Shield size={16} />
              <span>{t('nonRegistry.shareNudge')}</span>
            </div>
            <button className="btn btn-share" onClick={() => setShowShare(true)}>
              <Share2 size={18} />
              {t('share.shareMyRide')}
            </button>
          </div>
        ) : (
          <button
            className="btn btn-share btn-share-prominent"
            onClick={() => setShowShare(true)}
          >
            <Share2 size={18} />
            {t('share.shareMyRide')}
          </button>
        )}

        {/* Share Modal */}
        {showShare && (
          <ShareRide result={result} onClose={() => setShowShare(false)} />
        )}
      </div>
    );
  }

  return (
    <div className={`result-card ${isTLC ? 'result-found' : 'result-not-found'}`}>
      <div className="result-header">
        {isTLC ? (
          <>
            <CheckCircle className="result-icon success" />
            <div>
              <h3>{getLicensedLabel()}</h3>
              <p>{getRegisteredLabel()}</p>
            </div>
          </>
        ) : (
          <>
            <AlertCircle className="result-icon danger" />
            <div>
              <h3>{t('result.notLicensed')}</h3>
              <p>{t('result.notInRegistry')}</p>
            </div>
          </>
        )}
      </div>

      {/* Car Image/Placeholder */}
      {vehicleInfo && (
        <div className="car-image-container">
          {carImageUrl && (
            <>
              <div className="car-image-shimmer shimmer" />
              <img
                src={carImageUrl}
                alt={`${vehicleInfo?.year} ${vehicleInfo?.make} ${vehicleInfo?.model}`}
                onLoad={(e) => {
                  e.target.classList.add('loaded');
                  // Hide shimmer when image loads
                  const shimmer = e.target.previousSibling;
                  if (shimmer) shimmer.style.display = 'none';
                }}
                onError={(e) => {
                  e.target.style.display = 'none';
                  // Hide shimmer on error too
                  const shimmer = e.target.previousSibling;
                  if (shimmer) shimmer.style.display = 'none';
                  e.target.nextSibling.classList.add('visible');
                }}
              />
            </>
          )}
          {/* Car silhouette placeholder - shown when no image or image fails */}
          <div className={`car-placeholder ${!carImageUrl ? 'visible' : ''}`}>
            <svg viewBox="0 0 640 280" className="car-silhouette">
              <path d="M120,200 L140,140 Q160,100 220,90 L420,90 Q480,100 510,140 L580,180 Q600,190 600,210 L600,220 L580,220 L570,200 Q560,180 520,180 L120,180 Q80,180 70,200 L60,220 L40,220 L40,210 Q40,190 60,180 Z" fill="#374151"/>
              <circle cx="160" cy="220" r="40" fill="#1f2937" stroke="#4b5563" strokeWidth="8"/>
              <circle cx="160" cy="220" r="15" fill="#6b7280"/>
              <circle cx="480" cy="220" r="40" fill="#1f2937" stroke="#4b5563" strokeWidth="8"/>
              <circle cx="480" cy="220" r="15" fill="#6b7280"/>
              <path d="M180,100 L230,95 L230,130 Q200,135 180,140 Z" fill="#60a5fa" opacity="0.6"/>
              <path d="M420,95 L470,100 L450,140 Q430,135 420,130 Z" fill="#60a5fa" opacity="0.6"/>
              <path d="M260,95 L380,95 L380,130 L260,130 Z" fill="#60a5fa" opacity="0.6"/>
              <rect x="560" y="145" width="30" height="20" rx="3" fill="#ef4444"/>
              <rect x="55" y="165" width="25" height="15" rx="3" fill="#fbbf24"/>
            </svg>
          </div>
          <div className="car-image-label">
            {vehicleInfo.year} {vehicleInfo.make} {vehicleInfo.model}
            {vehicleInfo.color && <span className="car-color"> ({vehicleInfo.color})</span>}
          </div>
        </div>
      )}

      {/* Upgrade Prompt for Basic Lookups */}
      {isBasicLookup && isTLC && !carImageUrl && !isIOS() && (
        <div className="upgrade-prompt compact">
          <Lock size={18} />
          <div>
            <strong>{t('upgrade.unlockTitle') || 'See the actual car photo'}</strong>
            <p>{t('upgrade.unlockDesc') || 'Upgrade to verify you\'re getting in the right vehicle'}</p>
          </div>
          <Link to="/pricing" className="btn btn-yellow btn-small">
            {t('upgrade.unlock') || 'Unlock'}
          </Link>
        </div>
      )}

      {/* Enhanced Lookup Counter for Paid Users */}
      {!isBasicLookup && typeof enhancedRemaining === 'number' && (
        <div className="enhanced-counter">
          <Zap size={14} />
          <span>{enhancedRemaining} enhanced lookups remaining this month</span>
        </div>
      )}

      {/* Vehicle Info */}
      {vehicleInfo && (
        <div className="vehicle-info-card">
          <h4>{t('result.vehicleInfo')}</h4>
          <div className="info-grid">
            <div>
              <span className="label">{t('result.plate')}:</span>
              <span className="value plate-number">{result.plate}</span>
            </div>
            <div>
              <span className="label">{t('result.year')}:</span>
              <span className="value">{vehicleInfo.year}</span>
            </div>
            <div className="full-width">
              <span className="label">{t('result.vehicle')}:</span>
              <span className="value">{vehicleInfo.make} {vehicleInfo.model}</span>
            </div>
            {vehicleInfo.body_class && (
              <div className="full-width">
                <span className="label">{t('result.body')}:</span>
                <span className="value">{vehicleInfo.body_class}</span>
              </div>
            )}
            {vehicleInfo.color && (
              <div className="full-width">
                <span className="label">{t('result.color')}:</span>
                <span className="value">
                  {vehicleInfo.color}
                  {getColorSourceLabel() && (
                    <span className="color-source"> ({getColorSourceLabel()})</span>
                  )}
                </span>
              </div>
            )}
            {/* Show available colors if we have them and no registered color */}
            {colorInfo?.available_colors?.length > 1 && !colorInfo.registered_color && (
              <div className="full-width">
                <span className="label">{t('result.availableColors')}:</span>
                <span className="value available-colors">
                  {colorInfo.available_colors.slice(0, 5).join(', ')}
                  {colorInfo.available_colors.length > 5 && ` +${colorInfo.available_colors.length - 5} more`}
                </span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* License Info */}
      {isTLC && tlcData && (
        <div className="tlc-info-card">
          <h4>{getLicenseDetailsLabel()}</h4>
          <div className="info-grid">
            <div>
              <span className="label">{t('result.type')}:</span>
              <span className="value">{tlcData.license_type}</span>
            </div>
            <div>
              <span className="label">{t('result.status')}:</span>
              <span className={`value ${tlcData.active ? 'status-active' : 'status-inactive'}`}>
                {tlcData.active ? t('result.active') : t('result.expired')}
              </span>
            </div>
            <div className="full-width">
              <span className="label">{t('result.name')}:</span>
              <span className="value">{tlcData.name}</span>
            </div>
            <div className="full-width">
              <span className="label">{t('result.base')}:</span>
              <span className="value">{tlcData.base_name || tlcData.agent_name}</span>
            </div>
          </div>
        </div>
      )}

      {!isTLC && !vehicleInfo && (
        <div className="no-info">
          <p>{getNotFoundLabel()}</p>
        </div>
      )}

      {/* Result Disclaimer */}
      <div className="result-disclaimer">
        <AlertCircle size={12} />
        <span>{t('result.verifyDisclaimer')}</span>
      </div>

      {/* Safety Nudge + Share Button */}
      {result.vehicle_info && (
        <div className="share-nudge-section">
          <div className="share-nudge">
            <Shield size={16} />
            <span>Let someone know you're in this rideshare</span>
          </div>
          <button
            className="btn btn-share"
            onClick={() => setShowShare(true)}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="18" cy="5" r="3"></circle>
              <circle cx="6" cy="12" r="3"></circle>
              <circle cx="18" cy="19" r="3"></circle>
              <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"></line>
              <line x1="15.41" y1="6.51" x2="8.59" y2="10.49"></line>
            </svg>
            {t('share.shareMyRide')}
          </button>
        </div>
      )}

      {/* Share Modal */}
      {showShare && (
        <ShareRide result={result} onClose={() => setShowShare(false)} />
      )}
    </div>
  );
}
